var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Chip, CircularProgress, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { FixedSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { LoadingContainerStyled, OpitonListContainerStyled, OptionSelectAllStyled, OptionsEmptyStyled, TootipChipSelectStyled } from '@libs/@core/components/select/BaseSelect.styled';
import { CloseChipIcon, CloseIcon, EmptyOptionsIcon, SearchIcon } from '@import/icons';
var heightOption = 55;
var optionShowingNumDefault = 4;
/**
 * --------- Đây là base component select được dùng cho dự án ----------
 *
 * @component
 * @param value - Giá trị của select không dùng typescript là any
 *
 * @param options - Danh sách option trong select
 *
 * @param onChange - Hàm xử lý khi thay đổi giá trị
 *
 * @param rest - Các props khác của SelectMUI
 *
 * @param hasSearchBox - Hiển thị search box
 *
 * @param searchboxProps - Các props của search box
 *
 * @param multiple - Cho phép chọn nhiều giá trị dưa theo giá trị của value là mảng hoặc không
 *
 * @param optionShowingNum - Số lượng option hiển thị mặc định
 *
 * ------------------ Dùng cho call API ở Select ----------------------
 *
 * @param hasApi - Sử dụng API
 *
 * @param onFetchNextPage - Hàm fetch next page load more
 *
 * @param onFilter {(searchingValue: string) => void} Hàm filter: searching từ input search
 *
 * @param isLoadingOptions - Trạng thái loading khi lần đầu load các options
 *
 * @param hasNextPage - Kiểm tra xem còn trang tiếp theo không
 *
 * @param isFetchingNextPage - Trạng thái loading khi fetch next page
 *
 * @returns JSX.Element
 *
 * @author Pham Ngoc Mai Lam
 *
 */
var BaseSelect = function (props) {
    var value = props.value, options = props.options, hasSearchBox = props.hasSearchBox, searchboxProps = props.searchboxProps, _a = props.fullWidth, fullWidth = _a === void 0 ? true : _a, isLoadingOptions = props.isLoadingOptions, optionShowingNum = props.optionShowingNum, disabled = props.disabled, readOnly = props.readOnly, hasApi = props.hasApi, hasNextPage = props.hasNextPage, isFetchingNextPage = props.isFetchingNextPage, onChange = props.onChange, onFilter = props.onFilter, onFetchNextPage = props.onFetchNextPage, rest = __rest(props, ["value", "options", "hasSearchBox", "searchboxProps", "fullWidth", "isLoadingOptions", "optionShowingNum", "disabled", "readOnly", "hasApi", "hasNextPage", "isFetchingNextPage", "onChange", "onFilter", "onFetchNextPage"]);
    var selectRef = useRef(null);
    var searchBoxRef = useRef(null);
    var listRef = useRef(null);
    var hasMountedRef = useRef(false);
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var _c = useState(''), searchValue = _c[0], setSearchValue = _c[1]; // Search value
    var _d = useState(options), optionsBySearch = _d[0], setOptionsBySearch = _d[1];
    var _e = useState(0), numberTagname = _e[0], setNumberTagname = _e[1];
    var isValidValue = useMemo(function () {
        return Array.isArray(value) ? value.length !== 0 : !!value;
    }, [value]);
    // UseEffect for set number tagname and catch event resize
    useEffect(function () {
        if (!options && isLoadingOptions)
            return;
        setOptionsBySearch(options);
    }, [options]);
    useEffect(function () {
        if (!value || !Array.isArray(value) || !selectRef)
            return;
        setNumberTagname(getNumberTagname());
    }, [value]);
    useEffect(function () {
        if (!Array.isArray(value))
            return;
        var handleResize = function () {
            setNumberTagname(getNumberTagname());
        };
        window.addEventListener('resize', handleResize);
        return function () {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    // Each time the sort prop changed we called the method resetloadMoreItemsCache to clear the cache
    useEffect(function () {
        if (listRef.current && hasMountedRef.current) {
            listRef.current.resetloadMoreItemsCache();
        }
        hasMountedRef.current = true;
    }, [optionsBySearch]);
    // --------------------------------------------------------
    // ----------------- Action for search --------------------
    var handleSearch = function (event) {
        setSearchValue(event.target.value);
        if (hasApi) {
            onFilter && onFilter(event.target.value);
        }
        else {
            var opt = __spreadArray([], options, true).filter(function (option) {
                return option.label.toString().toLowerCase().includes(event.target.value.toLowerCase());
            });
            setOptionsBySearch(opt);
        }
    };
    var handleDelete = function (val) {
        if (!Array.isArray(value))
            return;
        onChange(__spreadArray([], value, true).filter(function (option) { return option !== val; }));
    };
    var handleChangeValue = function (e, option) {
        e.stopPropagation();
        if (props.multiple && Array.isArray(value)) {
            if (value.includes(option.value)) {
                onChange(__spreadArray([], value, true).filter(function (val) { return val !== option.value; }));
            }
            else {
                onChange(__spreadArray(__spreadArray([], value, true), [option.value], false));
            }
        }
        else {
            onChange(option.value);
            handleClose(e);
        }
    };
    var handleClearData = function () {
        onChange((props.multiple ? [] : ''));
    };
    // ---------------------------------------------------------
    // ------------ Action for open and select ----------------
    var handleOpen = function () {
        if (readOnly || disabled)
            return;
        setOpen(true);
    };
    var handleClose = function (e) {
        if (e.currentTarget.contains(searchBoxRef.current))
            return;
        setSearchValue('');
        setOptionsBySearch(options);
        setOpen(false);
    };
    // ---------------------------------------------------------
    // ----------------- Action for select all -----------------
    var handleCheckAll = function () {
        if (Array.isArray(value) && value.length === optionsBySearch.length) {
            onChange([]);
        }
        else {
            onChange(optionsBySearch.map(function (option) { return option.value; }));
        }
    };
    var getNumberTagname = function () {
        var _a;
        var totalTagShowing = 0;
        var listChipSelect = document.querySelectorAll('.chip-select');
        var widthInput = ((_a = selectRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) - 120;
        for (var i = 0; i < listChipSelect.length; i++) {
            var chipSelect = listChipSelect[i];
            if (chipSelect.offsetLeft + chipSelect.offsetWidth > widthInput) {
                break;
            }
            totalTagShowing++;
        }
        return totalTagShowing;
    };
    var renderValue = useCallback(function (selected) {
        var _a, _b;
        if (Array.isArray(selected)) {
            var selectdItemShowing = __spreadArray([], selected, true).slice(0, numberTagname);
            var renderChip_1 = function (value, index) {
                return function (_a) {
                    var className = _a.className;
                    var selectedItem = options === null || options === void 0 ? void 0 : options.find(function (option) { return option.value === value; });
                    return (_jsx(Chip, { onMouseDown: function (e) {
                            e.stopPropagation();
                        }, sx: {
                            margin: '2px',
                            paddingRight: '5px'
                        }, className: className, label: _jsx(TootipChipSelectStyled, { title: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.label, children: _jsx(Typography, { children: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.label }) }), onClick: handleOpen, onDelete: function () { return handleDelete(value); }, deleteIcon: _jsx(CloseChipIcon, { cursor: 'pointer' }) }, "chip-".concat(value, "-").concat(index)));
                };
            };
            return (_jsxs(_Fragment, { children: [_jsx(Box, { display: 'flex', width: '100%', visibility: 'hidden', position: 'absolute', overflow: 'hidden', children: selected.map(function (value, index) { return renderChip_1(value, index)({ className: 'chip-select' }); }) }), _jsxs(Box, { display: 'flex', children: [selectdItemShowing.map(function (value, index) { return renderChip_1(value, index)({ className: 'chip-select-showing' }); }), selectdItemShowing.length > 0 && selected.length - selectdItemShowing.length > 0 && (_jsx(Chip, { sx: {
                                    margin: '2px'
                                }, label: _jsxs(Typography, { children: ["+ ", selected.length - selectdItemShowing.length] }) }))] })] }));
        }
        return (_jsxs("div", { className: 'flex items-center gap-2', children: [options && ((_a = options === null || options === void 0 ? void 0 : options.find(function (option) { return option.value === selected; })) === null || _a === void 0 ? void 0 : _a.iconLabel), _jsx(Typography, { width: '100%', textOverflow: 'ellipsis', overflow: 'hidden', children: options && ((_b = options === null || options === void 0 ? void 0 : options.find(function (option) { return option.value === selected; })) === null || _b === void 0 ? void 0 : _b.label) })] }));
    }, [options, numberTagname]);
    // --------------------- Render Select API ---------------------------
    var itemCount = hasNextPage ? optionsBySearch.length + 1 : optionsBySearch.length;
    var loadMoreItems = onFetchNextPage && hasApi && hasNextPage
        ? function () {
            onFetchNextPage && onFetchNextPage();
        }
        : function () { };
    var isItemLoaded = function (index) {
        return isFetchingNextPage || !hasNextPage || index < optionsBySearch.length;
    };
    var Row = function (_a) {
        var index = _a.index, style = _a.style;
        if (!isItemLoaded(index) || !optionsBySearch[index]) {
            return (_jsx(LoadingContainerStyled, { style: style, children: _jsx(CircularProgress, { size: 20 }) }));
        }
        return (_jsxs(MenuItem, { value: optionsBySearch[index].value, onClick: function (e) { return handleChangeValue(e, optionsBySearch[index]); }, sx: __assign({ padding: '8px 16px', display: 'flex', gap: '10px' }, style), children: [props.multiple && Array.isArray(value) && (_jsx(Checkbox, { checked: value.includes(optionsBySearch[index].value) })), _jsx(Tooltip, { title: optionsBySearch[index].label, sx: {
                        flex: 1
                    }, placement: 'bottom-start', children: _jsxs("div", { className: 'flex items-center gap-2', children: [optionsBySearch[index].iconLabel && optionsBySearch[index].iconLabel, _jsx(Typography, { width: '100%', textOverflow: 'ellipsis', overflow: 'hidden', children: optionsBySearch[index].label })] }) })] }, "menu-item-".concat(optionsBySearch[index].value, "-").concat(index)));
    };
    return (_jsxs(Select, __assign({}, rest, { ref: selectRef, open: open, onOpen: handleOpen, onClose: handleClose, renderValue: renderValue, endAdornment: !disabled && isValidValue && !readOnly && _jsx(CloseIconAdornment, { onClick: handleClearData }), disabled: disabled, value: value !== null && value !== void 0 ? value : (props.multiple ? [] : ''), sx: __assign({ '& .MuiSelect-select span::before': {
                opacity: '0.4',
                content: ((Array.isArray(value) && value.length === 0) || !Array.isArray(value)) && rest.placeholder
                    ? "'".concat(rest.placeholder, "'")
                    : "''"
            } }, ((props === null || props === void 0 ? void 0 : props.sx) && __assign({}, props.sx))), MenuProps: {
            autoFocus: false,
            sx: {
                maxHeight: 500
            }
        }, fullWidth: fullWidth, children: [_jsxs(Box, { ref: searchBoxRef, width: '100%', children: [hasSearchBox && (_jsx(Box, { padding: 1, width: '100%', children: _jsx(TextField, __assign({ role: 'searchbox', id: 'search-box-select', autoFocus: true, InputProps: {
                                startAdornment: _jsx(SearchIcon, {})
                            }, value: searchValue, onChange: handleSearch, size: 'small', fullWidth: true }, (searchboxProps && searchboxProps))) })), props.multiple && Array.isArray(value) && optionsBySearch.length !== 0 && (_jsxs(OptionSelectAllStyled, { onClick: handleCheckAll, role: 'menuitem', children: [_jsx(Checkbox, { checked: value.length === optionsBySearch.length, indeterminate: value.length > 0 && value.length < optionsBySearch.length }), _jsx(Typography, { lineHeight: '15px', children: "Select All" })] }))] }), isLoadingOptions ? (_jsx(LoadingContainerStyled, { children: _jsx(CircularProgress, {}) })) : optionsBySearch.length === 0 ? (_jsx(BaseOptionsEmpty, {})) : (_jsx(OpitonListContainerStyled, { children: _jsx(InfiniteLoader, { ref: listRef, isItemLoaded: isItemLoaded, itemCount: itemCount, loadMoreItems: loadMoreItems, threshold: 5, children: function (_a) {
                        var onItemsRendered = _a.onItemsRendered, ref = _a.ref;
                        return (_jsx(List, { ref: ref, height: itemCount < (optionShowingNum !== null && optionShowingNum !== void 0 ? optionShowingNum : optionShowingNumDefault)
                                ? heightOption * itemCount
                                : heightOption * (optionShowingNum !== null && optionShowingNum !== void 0 ? optionShowingNum : optionShowingNumDefault), itemCount: itemCount, onItemsRendered: onItemsRendered, itemSize: heightOption, width: '100%', children: Row }));
                    } }) }))] })));
};
var CloseIconAdornment = function (props) {
    return (_jsx("div", __assign({ className: 'mr-6 flex items-center cursor-pointer' }, props, { children: _jsx(CloseIcon, {}) })));
};
var BaseOptionsEmpty = function () {
    return (_jsx(OptionsEmptyStyled, { children: _jsx(EmptyOptionsIcon, {}) }));
};
export default BaseSelect;
